import { isDefined, isNotDefined } from '@sgme/fp';
import { Spacer, VStack } from '@sgme/ui';
import deepEqual from 'fast-deep-equal';
import { memo, useCallback } from 'react';

import { selectCurrencyPairData } from '@/api/config/selectors.js';
import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import type { SendHelpRequestRequest } from '@/api/help-request/sendHelpRequest.ts';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  useAlgoIsChecking,
  useAlgoIsModifyingField,
  useAlgoSetIsModifyingField,
  useAlgoTileId,
  useValidateAlgo,
} from '@/App/tiles/orders/AlgoOrders/AlgoContext.js';
import { AlgoAlphaSeeker } from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoAlphaSeeker.tsx';
import { AlgoClippingMode } from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoClippingMode.tsx';
import { AlgoClipSize } from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoClipSize.tsx';
import { AlgoExpiryDate } from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoExpiryDate.tsx';
import { AlgoLimitPrice } from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoLimitPrice.tsx';
import { AlgoLiquidityPool } from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoLiquidityPool.tsx';
import AlgoMarketType from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoMarketType.tsx';
import AlgoNDFFixingDate from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoNDFFixingDate.tsx';
import AlgoNDFFixingSource from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoNDFFixingSource.tsx';
import { AlgoRandomize } from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoRandomize.tsx';
import { AlgoSpeed } from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoSpeed.tsx';
import { AlgoSpreadCapture } from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoSpreadCapture.tsx';
import { AlgoStartDate } from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoStartDate.tsx';
import { AlgoTenor } from '@/App/tiles/orders/AlgoOrders/AlgoOrderCreation/AlgoTenor.tsx';
import { ConnectedAlgoOrderSpot } from '@/App/tiles/orders/AlgoOrders/ConnectedAlgoOrderSpot.tsx';
import { OrderClientWay } from '@/App/tiles/orders/components/OrderClientWay.tsx';
import { OrderFooter } from '@/App/tiles/orders/components/OrderFooter.tsx';
import { OrderHeader } from '@/App/tiles/orders/components/OrderHeader.tsx';
import { OrderNotifications } from '@/App/tiles/orders/components/OrderNotifications.tsx';
import { OrderNotional } from '@/App/tiles/orders/components/OrderNotional.tsx';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { getConfig } from '@/config/config.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { useSubmitAlgoMutation } from '@/store/orders/algoOrders/api/index.ts';
import {
  selectAlgoClippingMode,
  selectAlgoCurrencyPair,
  selectAlgoErrors,
  selectAlgoNotificationEmails,
  selectAlgoNotionalData,
  selectAlgoTile,
  selectClientWayAlgoData,
} from '@/store/orders/algoOrders/selectors.ts';
import type { AlgoOrderRequest } from '@/store/orders/algoOrders/state.ts';
import type { AlgoOrderType, ValidationResponseDetailContent } from '@/store/orders/state.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';

export type AlgoOrderCreationTileProps = {
  algoType: AlgoOrderType;
};

/**
 * Memoized SwapTile component because the Grid rerenders a lot.
 */
export const AlgoOrderCreationTile = memo((props: AlgoOrderCreationTileProps) => {
  const track = useTrackEvent();
  const { algoType } = props;
  const tileId = useAlgoTileId();
  const setIsModifyingField = useAlgoSetIsModifyingField();
  const isModifyingField = useAlgoIsModifyingField();
  const dispatch = useAppDispatch();

  const notificationEmails = useAppSelector((state) => selectAlgoNotificationEmails(state, tileId));
  const notionalData = useAppSelector((state) => selectAlgoNotionalData(state, tileId));
  const clientWayData = useAppSelector((state) => selectClientWayAlgoData(state, tileId));
  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));

  const validate = useValidateAlgo();
  const [submitAlgo] = useSubmitAlgoMutation({
    selectFromResult: () => ({}),
  });

  const currencyPair = useAppSelector((state) => selectAlgoCurrencyPair(state, tileId));
  const currencyPairData = useAppSelector((state) => selectCurrencyPairData(state, currencyPair));
  const isNDF = currencyPairData?.notDeliverable ?? false;

  const isChecking = useAlgoIsChecking();

  const { areFieldsVisible, tile } = useAppSelector((state) => {
    const tile = selectAlgoTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        areFieldsVisible: false,
      };
    }

    return {
      tile,
      areFieldsVisible: isDefined(tile.properties.currencyPair),
    };
  }, deepEqual);

  const handleSubmit = useCallback(() => {
    track('Order action', 'Submit Order');
    if (isNotDefined(tile)) {
      console.error(`[handleSubmit] Tile with id ${tileId} not found in state.`);
      return;
    }

    if (isNotDefined(clientId)) {
      console.error('[handleSubmit] client id is not defined');
      return;
    }

    if (isNotDefined(notificationEmails)) {
      console.error('[handleSubmit] Notification emails not found in the tile or user preferences.');
      return;
    }

    if (
      isNotDefined(tile.properties.currencyPair) ||
      isNotDefined(tile.properties.notional) ||
      isNotDefined(tile.properties.limitPrice)
    ) {
      console.error('[handleSubmit] Currency pair is not defined in the tile.');
      return;
    }
    const TwapProperties =
      tile.type === 'Twap+Order'
        ? {
            clippingMode: tile.properties.clippingMode,
            spreadCapture: tile.properties.spreadCapture,
            clipSize: tile.properties.clipSize,
            randomize: tile.properties.randomize,
          }
        : undefined;

    const NightjarProperties =
      tile.type === 'NightjarOrder'
        ? {
            speed: tile.properties.speed,
            alphaSeeker: tile.properties.alphaSeeker,
          }
        : undefined;

    const FalconProperties =
      tile.type === 'FalconOrder'
        ? {
            speed: tile.properties.speed,
          }
        : undefined;

    submitAlgo({
      orderType: tile.type,
      tileId,
      currency1: tile.properties.currencyPair.currency1,
      currency2: tile.properties.currencyPair.currency2,
      clientId,
      way: tile.properties.clientWay,
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      limitPrice: tile.properties.limitPrice,
      notificationEmails: { emails: notificationEmails },
      tenor: tile.properties.tenor,
      startDate: tile.properties.startDate,
      expiryDate: tile.properties.expiryDate,
      ...TwapProperties,
      ...NightjarProperties,
      ...FalconProperties,
      liquidityPool: tile.properties.liquidityPool,
      isNdf: tile.properties.isNdf,
      clockOffset: tile.properties.clockOffset,
    } as AlgoOrderRequest)
      .unwrap()
      .catch(({ errors }: { type: 'failure'; errors: ValidationResponseDetailContent[] }) => {
        logger.logError('[ORDER] Failed to submit algo order: {errors_s}', errors);
        if (errors.some((error) => error.errorDescription === 'timeout')) {
          const params: SendHelpRequestRequest = {
            config: 'orderpending',
            details: JSON.stringify({
              payload: errors,
              orderIdToUpdate: tileId,
            }),
            data: {
              orderDescription: 'creation',
              orderType: 'algo',
              /**
               * send 'g10' or 'emerging' on order creation when we are able to differentiate
               */
              orderCurrencyType: 'unknown',
              orderId: tileId,
            },
            templateId: getConfig().app.mailTemplateIds.templateIdForPendingOrder,
            templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForPendingOrderConfirmation,
          };

          logger.logError('Order submission failed on timeout for {tileId_s}', tileId);
          dispatch(sendHelpRequestThunk(params));
        }
      });
  }, [tileId, clientId, notificationEmails, tile, submitAlgo]);

  return (
    <AutoAdvanceForm className="d-flex w-100 h-100">
      <VStack className="bg-lvl1 py-2" fullWidth fullHeight>
        <OrderHeader orderType={algoType} tile={tile} tileId={tileId} validate={validate} />

        {areFieldsVisible && (
          <>
            <div
              className="mx-16px"
              style={{
                display: 'grid',
                gap: '5px',
                gridTemplateColumns: 'auto minmax(0, 1fr)',
                gridTemplateRows: '20px',
                alignItems: 'center',
              }}
            >
              <ConnectedAlgoOrderSpot />
              <OrderClientWay clientWayData={clientWayData} validate={validate} />
              <OrderNotional
                errors={errors}
                notionalData={notionalData}
                validate={validate}
                setIsModifyingField={setIsModifyingField}
              />
              {isNDF && (
                <>
                  <AlgoNDFFixingSource />
                  <AlgoNDFFixingDate />
                  <AlgoMarketType />
                </>
              )}
              <AlgoTenor />
              <AlgoStartDate />
              <AlgoExpiryDate />
              <AlgoLiquidityPool />
              <AlgoLimitPrice />
              <AlgoTypeFields algoType={algoType} />
              <OrderNotifications
                tileId={tileId}
                errors={errors}
                notificationEmails={notificationEmails}
                validate={validate}
              />
            </div>
            <Spacer />

            <OrderFooter tile={tile} isModifyingField={isModifyingField} handleSubmit={handleSubmit} />
          </>
        )}
      </VStack>
      {isChecking && (
        <div className="position-absolute z-3 top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-lvl1 bg-opacity-75">
          <div className="spinner blinker-lg" />
        </div>
      )}
    </AutoAdvanceForm>
  );
});

const FalconFields = memo(() => <AlgoSpeed />);

const NightjarFields = memo(() => (
  <>
    <AlgoSpeed />
    <AlgoAlphaSeeker />
  </>
));

const TwapFields = memo(() => {
  const tileId = useAlgoTileId();
  const clippingMode = useAppSelector((state) => selectAlgoClippingMode(state, tileId));

  return (
    <>
      <AlgoClippingMode />
      {clippingMode === 'Manual' && <AlgoClipSize />}
      <AlgoRandomize />
      <AlgoSpreadCapture />
    </>
  );
});

const AlgoTypeFields = memo(({ algoType }: { algoType: AlgoOrderType }) => {
  switch (algoType) {
    case 'FalconOrder':
      return <FalconFields />;
    case 'NightjarOrder':
      return <NightjarFields />;
    case 'Twap+Order':
      return <TwapFields />;
    default:
      return null;
  }
});
